import {PropsWithChildren, useState} from "react";
import {createContext} from "../../../../../context/create-context";
import {useGetKycStatus} from "../../../../../react-query-hooks/kyc";
import {useKycDisplayStatus} from "../../../../../features/kyc/hooks";
import axios from "axios";
import {GetUserKycApiResponse} from "../../../../../react-query-hooks/kyc/types";

type GetKycStatusState = {
  kycStatusData: GetUserKycApiResponse | null;
  isFetchingKycStatus: boolean;
  isLoadingKycStatus: boolean;
  kycStatusError: string | null;
  kycStatusErrorCode: number | undefined;
  kycStatus: "no_entry" | "pending" | "successful" | "rejected";
  kycStatusOverride: boolean;
  setKycStatusOverride: (override: boolean) => void;
};

type GetKycStatusContextState = {
  state: GetKycStatusState;
};

const [useContext, ContextProvider] = createContext<GetKycStatusContextState>();

export const useGetKycStatusContext = useContext;

export const GetKycStatusProvider = ({children}: PropsWithChildren) => {
  const [kycStatusOverride, setKycStatusOverride] = useState(false);

  // New optimized hook: skips /kyc call for verified users
  const {displayStatus, isLoading: displayStatusLoading} = useKycDisplayStatus();

  // Legacy hook: still needed for kycStatusData (form pre-filling) when unverified
  // Disabled for verified users (displayStatus === "successful") to skip /kyc call
  const shouldFetchLegacyKyc = !displayStatusLoading && displayStatus !== "successful";
  const getKycStatusQuery = useGetKycStatus(shouldFetchLegacyKyc);
  const axiosError = axios.isAxiosError(getKycStatusQuery.error);
  const statusCode = getKycStatusQuery.error?.response?.status;
  const errorMessage =
    getKycStatusQuery.isError && axiosError ? getKycStatusQuery.error.message : null;

  const state: GetKycStatusState = {
    kycStatusData: getKycStatusQuery.data ? getKycStatusQuery.data : null,
    isFetchingKycStatus: getKycStatusQuery.isFetching,
    isLoadingKycStatus: displayStatusLoading,
    kycStatusError: errorMessage,
    kycStatusErrorCode: statusCode,
    kycStatus: displayStatus,
    kycStatusOverride,
    setKycStatusOverride,
  };

  return <ContextProvider value={{state}}>{children}</ContextProvider>;
};
